/**
 * PostProcessPhaseTween — blends the post-process grade between two
 * presets over the same window ConservatoryCycle lerps its lights (Q-034).
 *
 * Before this, the L2 Incubation/Irrigation flip lerped lights over
 * 1800ms but SNAPPED the full grade (exposure 1.4→0.82, contrast,
 * vignette, color curves) in one frame when CONSERVATORY_PHASE_CHANGED
 * fired at lerp end — a visible pop.
 *
 * Event-agnostic on purpose so the behavior is unit-testable and the
 * scene stays event-driven:
 *   - `start()` on CONSERVATORY_CYCLE_TRANSITION (lerp begin),
 *   - `update()` from the scene game loop with the same dtMs fed to
 *     ConservatoryCycle.update,
 *   - `settle()` on CONSERVATORY_PHASE_CHANGED — covers setPhaseImmediate
 *     (force-solve / checkpoint seeding emits no transition event) and
 *     pins exact final values after a normal lerp.
 *
 * The per-frame path goes through lerpPostProcessPreset, which writes
 * values only — no *Enabled flag toggles (those change shader defines and
 * force effect recompiles = stutter). Flags are set once by the canonical
 * applyPostProcessPreset at settle.
 */
import type { DefaultRenderingPipeline } from "@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline";
import {
  applyPostProcessPreset,
  lerpPostProcessPreset,
  type PostProcessPreset,
} from "../VisualProfile";

/**
 * Mirrors ConservatoryCycle's light-lerp easing (ConservatoryCycle.update)
 * so grade and lights move in lockstep.
 */
function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

export class PostProcessPhaseTween {
  private from: PostProcessPreset;
  private to: PostProcessPreset | null = null;
  private elapsedMs = 0;
  private durationMs = 0;

  constructor(
    private pipeline: DefaultRenderingPipeline,
    initial: PostProcessPreset,
  ) {
    this.from = initial;
  }

  /** Begin blending toward `target` over `durationMs` (from CONSERVATORY_CYCLE_TRANSITION). */
  start(target: PostProcessPreset, durationMs: number): void {
    if (target === this.from && !this.to) return;
    this.to = target;
    this.elapsedMs = 0;
    this.durationMs = Math.max(1, durationMs);
  }

  /**
   * Snap to `target` exactly — cancels any running tween. Fired on
   * CONSERVATORY_PHASE_CHANGED (covers setPhaseImmediate, which never
   * emits a transition event, and pins exact final values after a lerp).
   */
  settle(target: PostProcessPreset): void {
    this.to = null;
    this.from = target;
    applyPostProcessPreset(this.pipeline, target);
  }

  /** Tick from the scene game loop with the same dtMs fed to ConservatoryCycle.update. */
  update(dtMs: number): void {
    if (!this.to) return;
    this.elapsedMs += dtMs;
    const t = Math.min(this.elapsedMs / this.durationMs, 1);
    lerpPostProcessPreset(this.pipeline, this.from, this.to, easeInOutQuad(t));
    if (t >= 1) this.settle(this.to);
  }
}
